import {
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Chart as ChartJS,
  ChartData,
  ScriptableContext,
} from "chart.js";
import classNames from "classnames";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { TypedChartComponent } from "react-chartjs-2/dist/types";
import styles from "./AscBaselineAssessmentResultChart.module.scss";
import { camelToSpace, convertUnderlineToSpace } from "@ascendnow/shared";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export interface IChartData {
  title: string;
  data: {
    blurb?: string;
    score: number;
  };
}

export interface AscBaselineAssessmentResultChartProps {
  className?: string;
  firstAttemptData: IChartData[];
  lastAttemptData?: IChartData[];
  globalAverageData?: IChartData[];
  label1?: string;
  label2?: string;
  label3?: string;
  title: string;
  subtitle?: string;
  loading?: boolean;
  chartClassName?: string;
  labelListClassname?: string;
}

export default function AscBaselineAssessmentResultChart({
  className,
  firstAttemptData: _dataProp,
  lastAttemptData: _compareDataProp,
  globalAverageData: _globalDataProp,
  label1 = "Attempt 1",
  label2 = "Attempt 2",
  label3 = "Global Average",
  title,
  subtitle,
  loading = false,
  chartClassName,
  labelListClassname,
}: AscBaselineAssessmentResultChartProps) {
  const data: ChartData<"bar", number[], string> = useMemo(
    () => ({
      labels:
        _dataProp?.map((d) => {
          switch (d.title) {
            case "presentationSkills":
              return "Presentation";
            case "visualCommunication":
              return "Visual";
            case "writtenCommunication":
              return "Written";
            case "verbalNonVerbalCommunication":
              return "Verbal Nonverbal";
            default:
              //formatting camel case to space and then capilatalizing the first letter
              return convertUnderlineToSpace(camelToSpace(d.title));
          }
        }) ?? [],
      datasets: [
        ...(_dataProp?.map((d, index) => {
          const data = new Array(_dataProp?.length).fill(0);
          data.splice(index, 1, d.data.score);
          return {
            label: label1,
            data,
            backgroundColor: linearGradient("#FFE9CF", "#ff9b04"),
            order: index * 2,
            stack: "1",
          };
        }) ?? []),
        ...(_compareDataProp?.map((d, index) => {
          const data = new Array(_compareDataProp?.length).fill(0);
          data.splice(index, 1, d.data.score);
          return {
            label: label2,
            data,
            backgroundColor: linearGradient("#C3B6FD", "#4239fb"),
            order: index * 2,
            stack: "1",
          };
        }) ?? []),
        ...(_globalDataProp?.map((d, index) => {
          const data = new Array(_globalDataProp?.length).fill(0);
          data.splice(index, 1, d.data.score);
          return {
            label: label3,
            data,
            backgroundColor: "#61D5EE",
            order: index * 2,
            stack: "2",
          };
        }) ?? []),
      ],
    }),
    [_dataProp, _compareDataProp, _globalDataProp, label2, label1, label3]
  );
  const options: Parameters<TypedChartComponent<"bar">>[0]["options"] = useMemo(
    () => ({
      type: "bar",
      responsive: true,
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || "";

              if (label) {
                label += ": ";
              }
              if (context.parsed.y !== null) {
                label += `${context.parsed.y}%`;
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          stacked: true,
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            color: "#5E5E73",
            font: {
              family: "Poppins",
              size: 10,
              weight: "bolder",
              lineHeight: 4,
            },
          },
        },
        y: {
          stacked: true,
          border: {
            dash: [1, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            // Include a percentage sign in the ticks
            callback: function (value) {
              return `${value}%`;
            },
            color: "#9499B5",
            font: {
              family: "Poppins",
              size: 9,
              weight: "bolder",
              lineHeight: 4,
            },
          },
          max: 100,
        },
      },
      maintainAspectRatio: false,
      size: {
        width: "100%",
        height: "100%",
      },
    }),
    []
  );
  return (
    <div
      data-testid="asc-baseline-assessment-chart"
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
        },
        className
      )}
    >
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <h4 className={styles.title}>{title}</h4>
          {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
        <div className={classNames(styles.legend, labelListClassname)}>
          {label1 && (
            <div className={styles.legendItem}>
              <div
                className={styles.legendItemColor}
                style={{
                  background:
                    "linear-gradient(360deg, #FFE9CF -50%, #ff9b04 150%)",
                }}
              />
              <div className={styles.legendItemLabel}>{label1}</div>
            </div>
          )}
          {label2 && (
            <div className={styles.legendItem}>
              <div
                className={styles.legendItemColor}
                style={{
                  background:
                    "linear-gradient(360deg, #C3B6FD -50%, #4239fb 150%)",
                }}
              />
              <div className={styles.legendItemLabel}>{label2}</div>
            </div>
          )}
          {label3 && (
            <div className={styles.legendItem}>
              <div
                className={styles.legendItemColor}
                style={{
                  background:
                    "linear-gradient(360deg, #61D5EE -50%, #61D5EE 150%)",
                }}
              />
              <div className={styles.legendItemLabel}>{label3}</div>
            </div>
          )}
        </div>
      </header>
      <div className={classNames(styles.chartContainer, chartClassName)}>
        <Bar options={options} data={data} className={styles.barChart} />
      </div>
    </div>
  );
}

function linearGradient(startColor: string, endColor: string) {
  return function (context: ScriptableContext<"bar">) {
    const chart = context.chart;
    const { ctx, chartArea } = chart;

    if (!chartArea) {
      // This case happens on initial chart load
      return;
    }
    const gradient = ctx.createLinearGradient(
      0,
      chartArea.bottom,
      0,
      chartArea.top
    );
    gradient.addColorStop(0, startColor);
    gradient.addColorStop(1, endColor);
    return gradient;
  };
}
